import { useState } from 'react';

import { ArrowLeftIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { BrainVersionList } from '@/components/atoms/BrainVersionSelector';
import { MenuSearchRow } from '@/components/atoms/MenuSearchRow/MenuSearchRow';
import { Button } from '@/components/ui/button';
import { Command } from '@/components/ui/command';
import {
  PopoverComboboxEmpty,
  PopoverComboboxGroup,
  PopoverComboboxItem,
  PopoverComboboxList,
  PopoverComboboxOption,
} from '@/components/ui/custom/PopoverCombobox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Action, ActionType } from '@/shared/types/rules';
import { isKeyEnter } from '@/util/util';
import { isValidSlug } from '@/util/validation/patterns';

import { ACTION_LABEL_KEYS, MENU_PANEL_CLASS } from './constants';
import { useBundleOptions } from './useBundleOptions';
import { useCollectionOptions } from './useCollectionOptions';
import { useDepartmentOptions } from './useDepartmentOptions';
import { toAiAgentAction } from './utils';

type ActionEditorProps = {
  action: Action;
  onPick: (action: Action) => void;
  onBack?: () => void;
  commitOnBlur?: boolean;
};

type OptionListProps = ActionEditorProps & {
  field: 'department_id' | 'bundle_id' | 'collection_id';
  placeholder: string;
  options: PopoverComboboxOption[];
  isLoading: boolean;
};

const OptionList = ({
  action,
  onPick,
  onBack,
  field,
  placeholder,
  options,
  isLoading,
}: OptionListProps) => {
  const { t } = useTranslation();

  return (
    <Command className={MENU_PANEL_CLASS}>
      <MenuSearchRow placeholder={placeholder} onBack={onBack} />
      <PopoverComboboxList>
        {!isLoading && <PopoverComboboxEmpty>{t('common.no_results')}</PopoverComboboxEmpty>}
        <PopoverComboboxGroup>
          {isLoading && <Skeleton className="h-8 w-full" />}
          {options.map((option) => (
            <PopoverComboboxItem
              key={option.value}
              value={option.label}
              selected={option.value === action[field]}
              onSelect={() => onPick({ type: action.type, [field]: option.value })}
            >
              {option.label}
            </PopoverComboboxItem>
          ))}
        </PopoverComboboxGroup>
      </PopoverComboboxList>
    </Command>
  );
};

const DepartmentEditor = (props: ActionEditorProps) => {
  const { t } = useTranslation();
  const options = useDepartmentOptions();
  return (
    <OptionList
      {...props}
      {...options}
      field="department_id"
      placeholder={t('rules.editor.search_department')}
    />
  );
};

const BundleEditor = (props: ActionEditorProps) => {
  const { t } = useTranslation();
  const options = useBundleOptions();
  return (
    <OptionList
      {...props}
      {...options}
      field="bundle_id"
      placeholder={t('rules.editor.search_bundle')}
    />
  );
};

const CollectionEditor = (props: ActionEditorProps) => {
  const { t } = useTranslation();
  const options = useCollectionOptions();
  return (
    <OptionList
      {...props}
      {...options}
      field="collection_id"
      placeholder={t('rules.editor.search_collection')}
    />
  );
};

const AiAgentEditor = ({ action, onPick, onBack }: ActionEditorProps) => (
  <BrainVersionList
    className={MENU_PANEL_CLASS}
    selectedBrainId={action.brain_parent_id}
    selectedVersion={action.brain_version}
    searchPlaceholder="rules.editor.search_ai_agent"
    onBack={onBack}
    onSelect={(selection) => onPick(toAiAgentAction(selection))}
  />
);

const TagEditor = ({ action, onPick, onBack, commitOnBlur = false }: ActionEditorProps) => {
  const { t } = useTranslation();
  const [tag, setTag] = useState(action.tag ?? '');
  const isInvalid = Boolean(tag) && !isValidSlug(tag);

  const commit = () => {
    if (tag && !isInvalid && tag !== action.tag) {
      onPick({ type: action.type, tag });
    }
  };

  return (
    <div className={MENU_PANEL_CLASS}>
      <div className="flex h-10 shrink-0 items-center gap-1.5 py-2 pr-3 pl-2.5">
        {onBack && (
          <Button
            variant="ghost-neutral"
            size="icon-xs"
            aria-label={t('common.back')}
            onClick={onBack}
          >
            <ArrowLeftIcon />
          </Button>
        )}
        <span className="text-sm leading-6 font-medium">{t(ACTION_LABEL_KEYS[action.type])}</span>
      </div>
      <div className="flex flex-col gap-1 px-3 pt-2 pb-4">
        <Input
          autoFocus
          value={tag}
          placeholder={t('rules.type_tag')}
          aria-label={t('rules.tag')}
          aria-invalid={isInvalid}
          onChange={(e) => setTag(e.target.value.trim())}
          onKeyDown={(e) => {
            if (isKeyEnter(e)) {
              e.preventDefault();
              commit();
            }
          }}
          onBlur={commitOnBlur ? commit : undefined}
        />
        {isInvalid && <p className="text-sm text-warning">{t('rules.tags_rules')}</p>}
      </div>
    </div>
  );
};

export const ActionEditor = (props: ActionEditorProps) => {
  switch (props.action.type) {
    case ActionType.ASSIGN_BRAIN:
      return <AiAgentEditor {...props} />;
    case ActionType.ASSIGN_DEPARTMENT:
      return <DepartmentEditor {...props} />;
    case ActionType.APPLY_BUNDLE:
      return <BundleEditor {...props} />;
    case ActionType.ASSIGN_COLLECTION:
      return <CollectionEditor {...props} />;
    case ActionType.ADD_TAG:
      return <TagEditor {...props} />;
    default:
      return null;
  }
};
